/** Short, plain-English help panels for camera, location and installing the app. */
import type { ReactNode } from "react";
import { Spinner } from "../components/ui";
import { isAndroid, isIos } from "../lib/push";
import type { CameraProblem } from "../lib/scanner";

/** Shows the instructions for this phone first, and the other one under "Other phones". */
function ForPhone({ iphone, android }: { iphone: ReactNode; android: ReactNode }) {
  const ios = isIos();
  const droid = isAndroid();
  if (!ios && !droid) {
    return (
      <>
        <PhoneBlock title="iPhone">{iphone}</PhoneBlock>
        <PhoneBlock title="Android">{android}</PhoneBlock>
      </>
    );
  }
  return (
    <>
      {ios ? iphone : android}
      <details className="cl-details">
        <summary>{ios ? "On an Android phone?" : "On an iPhone?"}</summary>
        {ios ? android : iphone}
      </details>
    </>
  );
}

function PhoneBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="cl-phone">
      <strong className="cl-phone-title">{title}</strong>
      {children}
    </div>
  );
}

/** A path like Settings › Safari › Camera, with each step emphasised. */
export function Path({ steps }: { steps: string[] }) {
  return (
    <span className="cl-path">
      {steps.map((s, i) => (
        <span key={s}>
          {i > 0 && <span aria-hidden="true"> › </span>}
          <strong>{s}</strong>
        </span>
      ))}
    </span>
  );
}

export function CameraHelp({ problem }: { problem: CameraProblem }) {
  if (problem === "denied") {
    return (
      <div className="cl-help stack">
        <h2>The camera isn't allowed</h2>
        <ForPhone
          iphone={
            <>
              <p>
                iPhones ask every time you open the app. When it asks to use the camera, tap{" "}
                <strong>Allow</strong>.
              </p>
              <p>
                If it doesn't ask, go to <Path steps={["Settings", "Safari", "Camera"]} /> and choose{" "}
                <strong>Ask</strong> or <strong>Allow</strong>. Then close this app and open it again.
              </p>
            </>
          }
          android={
            <p>
              In Chrome, tap <strong>⋮</strong> then <Path steps={["Settings", "Site settings", "Camera"]} />{" "}
              and allow this app. Then try again.
            </p>
          }
        />
      </div>
    );
  }
  const text: Record<Exclude<CameraProblem, "denied">, string> = {
    no_camera: "We couldn't find a camera on this phone.",
    in_use: "Another app is using the camera. Close other apps and try again.",
    unsupported:
      "This browser can't use the camera. Try opening the app in Safari (iPhone) or Chrome (Android).",
    other: "The camera didn't start. Try again, or close the app and open it again.",
  };
  return (
    <div className="cl-help stack">
      <h2>The camera didn't start</h2>
      <p>{text[problem]}</p>
    </div>
  );
}

export type LocationProblem = "denied" | "imprecise" | "unavailable";

export function LocationHelp({ problem, compact }: { problem: LocationProblem; compact?: boolean }) {
  const heading = {
    denied: "Location is turned off for this app",
    imprecise: "Your location isn't precise enough",
    unavailable: "We couldn't find your location",
  }[problem];
  const steps = (
    <ForPhone
      iphone={
        <p>
          Go to{" "}
          <Path
            steps={[
              "Settings",
              "Privacy & Security",
              "Location Services",
              "Safari Websites",
              "While Using the App",
            ]}
          />
          , and turn on <strong>Precise Location</strong>.
        </p>
      }
      android={
        <p>
          In Chrome, tap <strong>⋮</strong> then <Path steps={["Settings", "Site settings", "Location"]} />{" "}
          and allow this app. In your phone's Settings, check Location is on and <strong>precise</strong>.
        </p>
      }
    />
  );
  const intro =
    problem === "unavailable"
      ? "Indoors this can take a while. Moving near a window can help."
      : "The office uses it to check you're at the site.";
  if (compact) {
    return (
      <details className="cl-details cl-help-compact">
        <summary>{heading} — how to fix</summary>
        <p>{intro}</p>
        {steps}
      </details>
    );
  }
  return (
    <div className="cl-help stack">
      <h2>{heading}</h2>
      <p>{intro}</p>
      {steps}
    </div>
  );
}

export function InstallHelp() {
  return (
    <ForPhone
      iphone={
        <ol className="cl-steps">
          <li>
            Open this page in <strong>Safari</strong>.
          </li>
          <li>
            Tap the <strong>Share</strong> button (the square with an arrow).
          </li>
          <li>
            Tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
          </li>
          <li>
            Open <strong>NKLEAN Hub</strong> from your Home Screen and sign in.
          </li>
        </ol>
      }
      android={
        <ol className="cl-steps">
          <li>
            Open this page in <strong>Chrome</strong>.
          </li>
          <li>
            Tap <strong>⋮</strong> (top right).
          </li>
          <li>
            Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.
          </li>
          <li>
            Open <strong>NKLEAN Hub</strong> from your Home screen and sign in.
          </li>
        </ol>
      }
    />
  );
}

export const PRIVACY_TEXT =
  "We only record your location at the moment you scan to clock in or out — never in the background.";

/**
 * The "Allow location" step. Its button asks for location from the tap itself, which is
 * when phones reliably show their permission prompt (and before the camera asks too).
 */
export function LocationPermissionStep({
  phase,
  onAllow,
  onSkip,
  onCancel,
}: {
  phase: "intro" | "waiting" | "denied";
  onAllow: () => void;
  onSkip: () => void;
  onCancel: () => void;
}) {
  if (phase === "waiting") {
    return (
      <div className="cl-sheet cl-centre stack">
        <Spinner label="Waiting for your phone" />
        <h1>Tap “Allow” on your phone's message</h1>
        <p className="muted">
          Your phone is asking whether NKLEAN Hub can use your location. On an iPhone, choose{" "}
          <strong>Allow While Using App</strong> (or <strong>Allow</strong>).
        </p>
        <button className="btn ghost block" onClick={onSkip}>
          No message appeared — carry on
        </button>
      </div>
    );
  }
  if (phase === "denied") {
    return (
      <div className="cl-sheet stack">
        <div className="cl-icon" aria-hidden="true">
          📍
        </div>
        <h1>Location is blocked</h1>
        <p className="cl-lead">
          Your phone has said no to location for NKLEAN Hub, so it won't ask again by itself. You can turn it
          back on in a few taps:
        </p>
        <LocationHelp problem="denied" />
        <ForPhone
          iphone={
            <p className="muted small">
              Using it as a Safari page rather than from the Home Screen? Tap <strong>aA</strong> in the
              address bar, then <Path steps={["Website Settings", "Location", "Allow"]} />.
            </p>
          }
          android={
            <p className="muted small">
              Or tap the icon to the left of the address in Chrome, then <strong>Permissions</strong> →{" "}
              <strong>Location</strong> → <strong>Allow</strong>.
            </p>
          }
        />
        <button className="btn primary huge" onClick={onAllow}>
          I've turned it on — try again
        </button>
        <button className="btn ghost block" onClick={onSkip}>
          Carry on without location
        </button>
        <p className="muted small">Without location, the office may need to approve your clock-in.</p>
      </div>
    );
  }
  return (
    <div className="cl-sheet stack">
      <div className="cl-icon" aria-hidden="true">
        📍
      </div>
      <h1>Allow location</h1>
      <p className="cl-lead">{PRIVACY_TEXT}</p>
      <p className="muted">
        It's used to check you're at the right site. When you tap the button, your phone will ask to share
        your location — please choose <strong>Allow</strong>.
      </p>
      <button className="btn primary huge" onClick={onAllow}>
        Allow location
      </button>
      <button className="btn ghost block" onClick={onCancel}>
        Not now
      </button>
    </div>
  );
}
